import { type FC, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/podkit/modal/Modal";
import { ConversationContent } from "@/components/ai/chat/AgentExecutionConversation";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { TrackLocations } from "@/hooks/use-segment";
import { usePendingMessages } from "@/hooks/use-pending-messages";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { Banner } from "@/components/flexkit/Banner";
import { formatError } from "@/utils/errors";
import { Text } from "@/components/podkit/typography/Text";
import { agentExecutionName } from "@/utils/agent-executions";
import { ChatInput } from "@/components/ai/chat/ChatInput";

type AgentExecutionModalProps = {
    agentExecution: PlainAgentExecution;
    open: boolean;
    onClose: () => void;
};

export const AgentExecutionModal: FC<AgentExecutionModalProps> = ({ agentExecution, open, onClose }) => {
    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const name = useMemo(() => agentExecutionName(agentExecution), [agentExecution]);
    const { pendingMessages, handleNewMessage } = usePendingMessages();
    const { error, messages, countOfUserBlocks, loadingMessage } = useStreamConversation(
        agentExecution.id,
        agentExecution?.status?.conversationUrl,
        pendingMessages,
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="h-[90vh] w-[90vw] max-w-[90vw]"
                data-track-location={TrackLocations.AgentExecutionModal}
            >
                <DialogHeader className="px-6 py-3">
                    <DialogTitle>{name}</DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <div className="flex flex-1 flex-col overflow-hidden">
                    {(error || loadingMessage) && (
                        <div className="flex flex-col gap-1">
                            {error && <Banner text={formatError(error)} variant="danger" className="mx-6 mb-0 mt-4" />}
                            {loadingMessage && (
                                <Text className="w-full text-center text-sm text-content-secondary">
                                    {loadingMessage}
                                </Text>
                            )}
                        </div>
                    )}
                    <ConversationContent messages={messages} agentExecution={agentExecution} />
                    <div className="relative flex w-full justify-center">
                        <ChatInput
                            agentExecution={agentExecution}
                            onNewMessage={(message) => handleNewMessage(message, countOfUserBlocks)}
                            placeholder="Reply"
                            className="max-w-[700px]"
                        />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
